import { PageSize } from '@/constant';
import {
	GetUpdateHistoryListParams,
	TGetAutocompleteSuggestionResponse,
} from '@/services';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer, REHYDRATE } from 'redux-persist';

import { DefaultOptionType } from 'antd/es/select';

import { IPayloadResetPasswordRequest, RootState } from '..';
import { storage } from '../store.persist';
import {
	IAppUserInfoStore,
	ICommonApplicantAssignmentListStore,
	ICommonApplicantListStore,
	ICommonPayloadUpdateRecommenderRequest,
	IPayloadCreateCommonAssignmentRequest,
	IPayloadCreateCommonPublicAssignmentRequest,
	IPayloadGetAppUserInfoRequest,
	IPayloadGetAppUserInfoSuccess,
	IPayloadGetAutocompleteSuggestionSuccess,
	IPayloadGetCommonApplicantAssignmentListRequest,
	IPayloadGetCommonApplicantAssignmentListSuccess,
	IPayloadGetCommonApplicantListRequest,
	IPayloadGetCommonApplicantListSuccess,
	IPayloadGetCommonListRequest,
	IPayloadGetCommonListSuccess,
	IPayloadGetDocumentJsonContentRequest,
	IPayloadGetDocumentMappingsRequest,
	IPayloadGetDocumentMappingsSuccess,
	IPayloadGetDocumentObjectDetailRequest,
	IPayloadGetDocumentObjectFailure,
	IPayloadGetDocumentObjectRequest,
	IPayloadGetDocumentObjectSuccess,
	IPayloadGetUpdateHistoryListRequest,
	IPayloadGetUpdateHistoryListSuccess,
	IPayloadGetYearRegisterFailure,
	IPayloadGetYearRegisterRequest,
	IPayloadGetYearRegisterSuccess,
	IPayloadSelectMasterInfoSuccess,
	IPayloadSetHeaderProgram,
	IPayloadUpdateFieldsRequest,
	IPayloadUploadFileRequest,
	IPayloadVerifyDomainRequest,
	IPayloadVerifyEmailRequest,
	IUpdateHistoryDetailStore,
	TCommonDocumentMappingsStore,
	TCommonGetFieldRequest,
	TDocumentObjectDetail,
	TDocumentObjectList,
	TPayloadGetSettingColumnFailure,
	TPayloadGetSettingColumnRequest,
	TPayloadGetSettingColumnSuccess,
	TPayloadSetSettingColumnRequest,
	TSelectCommonList,
	TSelectMasterInfo,
	TSettingColumn,
	TToast,
	TYearRegisterList,
} from './common.type';

const InitUpdateHistoryParams = {
	page: 1,
	pageSize: PageSize.S,
} as GetUpdateHistoryListParams;

const InitPaginationParams = {
	page: 1,
	pageSize: PageSize.XS,
} as IPayloadGetCommonApplicantListRequest['params'];

const initialState = {
	hasFormChanged: false,
	toast: {
		message: '',
		duration: 5000,
		type: 'SUCCESS',
		key: 0,
	} as TToast,
	getFieldsFetching: false,
	settingColumn: {} as TSettingColumn,
	fetching: false,

	selectMasterInfo: {} as TSelectMasterInfo,
	list: {} as TSelectCommonList,
	yearRegisterList: {} as TYearRegisterList,
	uploading: false,
	updateFields: false,
	headerProgram: {} as DefaultOptionType & { expiry?: number },
	documentObjectList: {} as TDocumentObjectList,
	documentObjectDetail: {} as TDocumentObjectDetail,

	updateHistoryFetching: false,
	updateHistoryList: [] as IUpdateHistoryDetailStore[],
	updateHistoryTotal: 0,
	updateHistoryParams: InitUpdateHistoryParams,

	autocompleteSuggestion: {} as TGetAutocompleteSuggestionResponse,
	autocompleteApplicantSuggestion: [] as DefaultOptionType[],

	fetchingApplicant: false,
	commonApplicantListParams: InitPaginationParams,
	commonApplicantListTotal: 0,
	commonApplicantList: [] as ICommonApplicantListStore[],
	commonApplicantAssignmentList: [] as ICommonApplicantAssignmentListStore[],

	fetchingGetAppUserInfo: false,
	appUserInfo: {} as IAppUserInfoStore,

	documentDetail: {
		jsonContent: {} as Record<string, any>,
		fetching: false,
	},

	fetchingDocumentMappings: false,
	documentMappings: {} as TCommonDocumentMappingsStore,
};

const commonSlice = createSlice({
	initialState,
	name: 'common',
	reducers: {
		setHasFormChange: (state, action: PayloadAction<boolean>) => {
			state.hasFormChanged = action.payload;
		},

		showToast: (state, action: PayloadAction<TToast>) => {
			state.toast = {
				...action.payload,
				key: new Date().getTime(),
			};
		},

		/**---- SET SETTING COLUMN START ---- */
		setSettingColumnRequest: (
			state,
			action: PayloadAction<TPayloadSetSettingColumnRequest>
		) => {},
		/**---- SET SETTING COLUMN END ---- */

		/**---- GET SETTING COLUMN LIST FIELDS START ---- */
		getSettingColumnRequest: (
			state,
			action: PayloadAction<TPayloadGetSettingColumnRequest>
		) => {
			const { screenCode } = action.payload;

			state.settingColumn = {
				...state.settingColumn,
				[screenCode]: {
					...(state.settingColumn?.[screenCode] || {}),
					fetching: true,
				},
			};
		},
		getSettingColumnSuccess: (
			state,
			action: PayloadAction<TPayloadGetSettingColumnSuccess>
		) => {
			const { screenCode, content } = action.payload;
			state.settingColumn = {
				...state.settingColumn,
				[screenCode]: {
					fetching: false,
					content,
				},
			};
		},
		getSettingColumnFailure: (
			state,
			action: PayloadAction<TPayloadGetSettingColumnFailure>
		) => {
			const { screenCode } = action.payload;
			state.settingColumn = {
				...state.settingColumn,
				[screenCode]: {
					fetching: false,
				},
			};
		},
		/**---- GET SETTING COLUMN LIST FIELDS END ---- */

		/**---- VERIFY EMAIL START ---- */
		verifyEmailRequest: (
			state,
			action: PayloadAction<IPayloadVerifyEmailRequest>
		) => {
			state.fetching = true;
		},
		verifyEmailSuccess: (state) => {
			state.fetching = false;
		},
		verifyEmailFailure: (state) => {
			state.fetching = false;
		},
		/**---- VERIFY EMAIL END ---- */

		/**---- VERIFY DOMAIN START ---- */
		verifyDomainRequest: (
			state,
			action: PayloadAction<IPayloadVerifyDomainRequest>
		) => {
			state.fetching = true;
		},
		verifyDomainSuccess: (state) => {
			state.fetching = false;
		},
		verifyDomainFailure: (state) => {
			state.fetching = false;
		},
		/**---- VERIFY DOMAIN END ---- */

		/**---- COMMON LIST START ---- */
		getListCommonRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonListRequest>
		) => {
			const { type } = action.payload;
			state.selectMasterInfo = {
				...state.selectMasterInfo,
				[type]: {
					fetching: true,
				},
			};
			state.list = {
				...state.list,
				[type]: {
					fetching: true,
				},
			};
		},
		getCommonListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonListSuccess>
		) => {
			const { type, data, total } = action.payload;
			state.list = {
				...state.list,
				[type]: {
					fetching: false,
					data,
					total,
				},
			};
		},
		getCommonListFailure: (state, action: PayloadAction<{ type: string }>) => {
			const { type } = action.payload;
			state.list = {
				...state.list,
				[type]: {
					fetching: false,
				},
			};
		},
		getCommonApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonApplicantListRequest>
		) => {
			state.fetchingApplicant = true;
			state.commonApplicantListParams = action?.payload?.params;
		},
		getCommonApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonApplicantListSuccess>
		) => {
			const { data, total } = action.payload;
			state.fetchingApplicant = false;
			state.commonApplicantList = data;
			state.commonApplicantListTotal = total;
		},
		getCommonApplicantListFailure: (state) => {
			state.fetchingApplicant = false;
			state.commonApplicantListTotal = 0;
			state.commonApplicantList = [];
		},

		getCommonApplicantWithIdsRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonApplicantListRequest>
		) => {
			state.fetchingApplicant = true;
		},
		getCommonApplicantWithIdsSuccess: (state) => {
			state.fetchingApplicant = false;
		},
		getCommonApplicantWithIdsFailure: (state) => {
			state.fetchingApplicant = false;
		},

		getCommonApplicantAssignmentListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonApplicantAssignmentListRequest>
		) => {
			state.fetching = true;
		},
		getCommonApplicantAssignmentListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonApplicantAssignmentListSuccess>
		) => {
			const { data } = action.payload;
			state.fetching = false;
			state.commonApplicantAssignmentList = data;
		},
		getCommonApplicantAssignmentListFailure: (state) => {
			state.fetching = false;
			state.commonApplicantAssignmentList = [];
		},

		selectMasterInfoSuccess: (
			state,
			action: PayloadAction<IPayloadSelectMasterInfoSuccess>
		) => {
			const { type, data } = action.payload;
			state.selectMasterInfo = {
				...state.selectMasterInfo,
				[type]: {
					fetching: false,
					data,
				},
			};
		},
		selectMasterInfoFailure: (
			state,
			action: PayloadAction<{ type: string }>
		) => {
			const { type } = action.payload;
			state.selectMasterInfo = {
				...state.selectMasterInfo,
				[type]: {
					fetching: false,
				},
			};
		},
		/**---- COMMON LIST END ---- */

		/**---- UPLOAD FILE START ---- */
		uploadFileRequest: (
			state,
			action: PayloadAction<IPayloadUploadFileRequest>
		) => {
			state.uploading = true;
		},

		commonFileUploadRequest: (
			state,
			action: PayloadAction<IPayloadUploadFileRequest>
		) => {},
		/**---- UPLOAD FILE END ---- */

		/**---- UPDATE FIELDS START ---- */
		updateFieldsRequest: (
			state,
			action: PayloadAction<IPayloadUpdateFieldsRequest>
		) => {
			state.updateFields = false;
		},
		/**---- UPDATE FIELDS END ---- */

		/**---- COMMON YEAR REGISTER START ---- */
		getYearRegisterListRequest: (
			state,
			action: PayloadAction<IPayloadGetYearRegisterRequest>
		) => {
			const { screenCode } = action.payload;
			state.yearRegisterList = {
				...state.yearRegisterList,
				[screenCode]: {
					fetching: true,
				},
			};
		},
		getYearRegisterListSuccess: (
			state,
			action: PayloadAction<IPayloadGetYearRegisterSuccess>
		) => {
			const { screenCode, data } = action.payload;
			state.yearRegisterList = {
				...state.yearRegisterList,
				[screenCode]: {
					fetching: false,
					data,
				},
			};
		},
		getYearRegisterListFailure: (
			state,
			action: PayloadAction<IPayloadGetYearRegisterFailure>
		) => {
			const { screenCode } = action.payload;
			state.yearRegisterList = {
				...state.yearRegisterList,
				[screenCode]: {
					fetching: false,
				},
			};
		},
		/**---- COMMON YEAR REGISTER END ---- */

		/**---- COMMON HEADER PROGRAM START ---- */
		setHeaderProgram: (
			state,
			action: PayloadAction<IPayloadSetHeaderProgram>
		) => {
			const { option, expiry } = action.payload;
			state.headerProgram = { ...option, expiry };
		},
		resetHeaderProgram: (state) => {
			state.headerProgram = {} as DefaultOptionType;
		},
		/**---- COMMON HEADER PROGRAM END ---- */

		/**---- COMMON DOCUMENT OBJECT LIST START ---- */
		getDocumentObjectListRequest: (
			state,
			action: PayloadAction<IPayloadGetDocumentObjectRequest>
		) => {
			const { refTableName } = action.payload;
			state.documentObjectList = {
				...state.documentObjectList,
				[refTableName]: {
					fetching: true,
				},
			};
		},
		getDocumentObjectListSuccess: (
			state,
			action: PayloadAction<IPayloadGetDocumentObjectSuccess>
		) => {
			const { refTableName, data } = action.payload;
			state.documentObjectList = {
				...state.documentObjectList,
				[refTableName]: {
					fetching: false,
					data,
				},
			};
		},
		getDocumentObjectListFailure: (
			state,
			action: PayloadAction<IPayloadGetDocumentObjectFailure>
		) => {
			const { refTableName } = action.payload;
			state.documentObjectList = {
				...state.documentObjectList,
				[refTableName]: {
					fetching: false,
				},
			};
		},
		/**---- COMMON DOCUMENT OBJECT LIST END ---- */

		/**---- COMMON DOCUMENT OBJECT DETAIL START ---- */
		getDocumentObjectDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetDocumentObjectDetailRequest>
		) => {
			state.documentObjectDetail = action.payload;
		},
		/**---- COMMON DOCUMENT OBJECT DETAIL END ---- */

		/**---- COMMON UPDATE HISTORY LIST START ---- */
		getUpdateHistoryListRequest: (
			state,
			action: PayloadAction<IPayloadGetUpdateHistoryListRequest>
		) => {
			state.updateHistoryFetching = true;
			state.updateHistoryParams = action.payload;
		},
		getUpdateHistoryListSuccess: (
			state,
			action: PayloadAction<IPayloadGetUpdateHistoryListSuccess>
		) => {
			const { content, total } = action.payload;
			state.updateHistoryFetching = false;
			state.updateHistoryList = content;
			state.updateHistoryTotal = total;
		},
		getUpdateHistoryListFailure: (state) => {
			state.updateHistoryFetching = false;
			state.updateHistoryList = [];
			state.updateHistoryTotal = 0;
		},
		/**---- COMMON UPDATE HISTORY LIST END ---- */
		/**---- COMMON AUTOCOMPLETE SUGGESTION START ---- */
		getAutocompleteSuggestionRequest: (state) => {
			state.fetching = true;
		},
		getAutocompleteSuggestionSuccess: (
			state,
			action: PayloadAction<IPayloadGetAutocompleteSuggestionSuccess>
		) => {
			const { data } = action.payload;

			state.fetching = false;
			state.autocompleteSuggestion = data;

			const applicantData = [...(data?.applicants || [])];
			state.autocompleteApplicantSuggestion = applicantData?.map((item) => ({
				id: item?.id,
				value: item?.id,
				label: item?.value,
			}));
		},
		getAutocompleteSuggestionFailure: (state) => {
			state.fetching = false;
		},
		/**---- COMMON AUTOCOMPLETE SUGGESTION END ---- */

		/**---- COMMON CREATE ASSIGNMENT START ---- */
		createCommonAssignmentRequest: (
			state,
			action: PayloadAction<IPayloadCreateCommonAssignmentRequest>
		) => {
			state.fetching = true;
		},
		createCommonAssignmentSuccess: (state) => {
			state.fetching = false;
		},
		createCommonAssignmentFailure: (state) => {
			state.fetching = false;
		},
		/**---- COMMON CREATE ASSIGNMENT END ---- */

		/**---- COMMON CREATE PUBLIC ASSIGNMENT START ---- */
		createCommonPublicAssignmentRequest: (
			state,
			action: PayloadAction<IPayloadCreateCommonPublicAssignmentRequest>
		) => {
			state.fetching = true;
		},
		createCommonPublicAssignmentSuccess: (state) => {
			state.fetching = false;
		},
		createCommonPublicAssignmentFailure: (state) => {
			state.fetching = false;
		},
		/**---- COMMON CREATE ASSIGNMENT END ---- */

		/**---- UPDATE RECOMMENDER POP UP START ---- */
		updateCommonRecommenderRequest: (
			state,
			action: PayloadAction<ICommonPayloadUpdateRecommenderRequest>
		) => {
			state.fetching = true;
		},
		updateCommonRecommenderSuccess: (state) => {
			state.fetching = false;
		},
		updateCommonRecommenderFailure: (state) => {
			state.fetching = false;
		},
		/**---- UPDATE RECOMMENDER POP UP END ---- */

		/**---- COMMON GET FIELDS START ---- */
		getFieldsRequest: (
			state,
			action: PayloadAction<TCommonGetFieldRequest>
		) => {
			state.getFieldsFetching = true;
		},
		getFieldsSuccess: (state) => {
			state.getFieldsFetching = false;
		},
		getFieldsFailure: (state) => {
			state.getFieldsFetching = false;
		},
		/**---- COMMON GET FIELDS END ---- */

		/**---- COMMON Json Content START ---- */
		getDocumentJsonContentRequest: (
			state,
			action: PayloadAction<IPayloadGetDocumentJsonContentRequest>
		) => {
			state.documentDetail = {
				jsonContent: {},
				fetching: true,
			};
		},

		getDocumentJsonContentSuccess: (state, action: PayloadAction<string>) => {
			state.documentDetail = {
				jsonContent: action.payload ? JSON.parse(action.payload) : {},
				fetching: false,
			};
		},

		getDocumentJsonContentFailure: (state) => {
			state.documentDetail = {
				jsonContent: {},
				fetching: false,
			};
		},

		resetDocumentJsonContent: (state) => {
			state.documentDetail = {
				jsonContent: {},
				fetching: false,
			};
		},
		/**---- COMMON Json Content END ---- */
		/**---- RESET PASSWORD START ---- */
		resetPasswordRequest: (
			state,
			action: PayloadAction<IPayloadResetPasswordRequest>
		) => {
			state.fetching = true;
		},
		resetPasswordSuccess: (state) => {
			state.fetching = false;
		},
		resetPasswordFailure: (state) => {
			state.fetching = false;
		},
		/**---- RESET PASSWORD END ---- */
		/**---- RESET PASSWORD START ---- */
		unWithdrawnRequest: (
			state,
			action: PayloadAction<IPayloadResetPasswordRequest>
		) => {
			state.fetching = true;
		},
		unWithdrawnSuccess: (state) => {
			state.fetching = false;
		},
		unWithdrawnFailure: (state) => {
			state.fetching = false;
		},
		/**---- RESET PASSWORD END ---- */
		/**---- GET APP USER INFO START ---- */
		getAppUserInfoRequest: (
			state,
			action: PayloadAction<IPayloadGetAppUserInfoRequest>
		) => {
			state.fetchingGetAppUserInfo = true;
		},
		getAppUserInfoSuccess: (
			state,
			action: PayloadAction<IPayloadGetAppUserInfoSuccess>
		) => {
			const { appUserInfo } = action.payload;
			state.fetchingGetAppUserInfo = false;
			state.appUserInfo = appUserInfo;
		},
		getAppUserInfoFailure: (state) => {
			state.fetchingGetAppUserInfo = false;
		},
		/**---- GET APP USER INFO END ---- */

		/**---- GET DOCUMENT MAPPINGS START ---- */
		getDocumentMappingsRequest: (
			state,
			action: PayloadAction<IPayloadGetDocumentMappingsRequest>
		) => {
			state.fetchingDocumentMappings = true;
		},
		getDocumentMappingsSuccess: (
			state,
			action: PayloadAction<IPayloadGetDocumentMappingsSuccess>
		) => {
			const { data } = action.payload;
			state.fetchingDocumentMappings = false;
			state.documentMappings = data;
		},
		getDocumentMappingsFailure: (state) => {
			state.fetchingDocumentMappings = false;
			state.documentMappings = {} as TCommonDocumentMappingsStore;
		},
		/**---- GET DOCUMENT MAPPINGS END ---- */
	},
	extraReducers(builder) {
		builder.addCase(REHYDRATE, (state) => {
			if (state.headerProgram.expiry) {
				const now = Date.now();

				if (state.headerProgram.expiry < now) {
					return {
						...state,
						headerProgram: {} as DefaultOptionType,
					};
				}

				return {
					...state,
				};
			}
		});
	},
});

export const CommonSelectors = {
	selectFormHasChanged: (state: RootState) => state.common.hasFormChanged,

	selectToastMessage: (state: RootState) => state.common.toast,

	selectSettingColumn: (state: RootState) => state.common.settingColumn,

	selectMasterInfo: (state: RootState) => state.common.selectMasterInfo,
	commonList: (state: RootState) => state.common.list,
	yearRegisterList: (state: RootState) => state.common.yearRegisterList,
	selectHeaderProgram: (state: RootState) => state.common.headerProgram,

	documentObjectList: (state: RootState) => state.common.documentObjectList,

	selectUpdateHistoryFetching: (state: RootState) =>
		state.common.updateHistoryFetching,
	selectUpdateHistoryParams: (state: RootState) =>
		state.common.updateHistoryParams,
	selectUpdateHistoryList: (state: RootState) => state.common.updateHistoryList,
	selectUpdateHistoryTotal: (state: RootState) =>
		state.common.updateHistoryTotal,

	selectAutocompleteSuggestion: (state: RootState) =>
		state.common.autocompleteSuggestion,

	selectAutocompleteApplicantSuggestion: (state: RootState) =>
		state.common.autocompleteApplicantSuggestion,

	selectFetchingCommonApplicantList: (state: RootState) =>
		state.common.fetchingApplicant,
	selectTotalCommonApplicantList: (state: RootState) =>
		state.common.commonApplicantListTotal,
	selectCommonApplicantList: (state: RootState) =>
		state.common.commonApplicantList,
	selectParamsCommonApplicantList: (state: RootState) =>
		state.common.commonApplicantListParams,

	selectJsonContent: (state: RootState) =>
		state.common.documentDetail.jsonContent,

	selectAppUserInfo: (state: RootState) => state.common.appUserInfo,
	selectFetchingGetAppUserInfo: (state: RootState) =>
		state.common.fetchingGetAppUserInfo,
};

export const CommonActions = commonSlice.actions;
export const COMMON_PERSIST_KEY = 'commonPersist';
export const CommonReducer = persistReducer(
	{
		key: COMMON_PERSIST_KEY,
		storage: storage,
		whitelist: ['headerProgram', 'settingColumn'],
	},
	commonSlice.reducer
);
